import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../utils/context/AuthContext'
import { Eye, EyeOff,  Mail } from 'lucide-react'
import axiosInstance from '../../utils/axios/axiosConfig'
const apiSanctum = process.env.REACT_APP_BACKEND_URL_SANCTUM

function Login() {
  const [view, setView] = useState('signin')
  const [loginData, setLoginData] = useState({ email: null, password: null, code: null })
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [codeVerify, setCodeVerify] = useState(false)
  const [changePasswordModal, setChangePasswordModal] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    navigate('/Login')
    axios.get(`${apiSanctum}/csrf-cookie`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      withCredentials: true,
    })
  }, [])

  const handleLogin = async (e) => {
    e.preventDefault()
    if (!loginData.email || !loginData.password) {
      setErrorMessage('Both email and password are required.')
      return
    }
    try {
      setErrorMessage('')
      setLoading(true)
      const response = await axiosInstance.post('/auth/login', loginData)
      const responseData = response.data

      if (responseData && responseData.success) {
        setCodeVerify(true)
      } else if (responseData && responseData.user) {
        const { token } = responseData
        login(token)
        navigate('/')
      }
      setLoading(false)
    } catch (error) {
      setErrorMessage(error.response.data.message)
      setLoading(false)
    }
  }

  const handleCodeVerification = async (e) => {
    e.preventDefault()
    if (!loginData.code) {
      setErrorMessage('The code is required.')
      return
    }
    try {
      setErrorMessage('')
      setLoading(true)
      const response = await axiosInstance.post('/auth/code-verification', loginData)
      const responseData = response.data
      if (responseData && responseData.user) {
        const { token } = responseData
        setCodeVerify(false)
        login(token)
        navigate('/')
      }
      setLoading(false)
    } catch (error) {
      setErrorMessage(error.response.data.message)
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    try {
      setErrorMessage('')
      setLoading(true)
      const response = await axiosInstance.post('/auth/forget-passKey', loginData)
      const responseData = response.data
      if (responseData) {
        setCodeVerify(true)
      }
      setLoading(false)
    } catch (error) {
      setErrorMessage(error.response.data.message)
      setLoading(false)
    }
  }

  const handleForgetPassCodeVerification = async (e) => {
    e.preventDefault()
    try {
      setErrorMessage('')
      setLoading(true)
      const response = await axiosInstance.post('/auth/verify-code', loginData)
      const responseData = response.data
      if (responseData) {
        setCodeVerify(false)
        setChangePasswordModal(true)
        setView('signin')
      }
      setLoading(false)
    } catch (error) {
      setErrorMessage(error.response.data.message)
      setLoading(false)
    }
  }



  return (
    <motion.div
      initial={{ opacity: 0, scale: 1.05 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      className="flex h-screen w-screen items-center justify-center overflow-hidden bg-white dark:bg-neutral-900"
    >
      <div className="flex h-full w-full items-center justify-center">
        <div className="hidden md:block md:w-3/5  h-full bg-neutral-900 dark:bg-neutral-800 relative">
          <div className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://artstride-agency.b-cdn.net/SLIDER%20SITE%20WEB2.jpg)`, opacity: 0.8 }}></div>
          <div className="absolute inset-0 flex items-center justify-center">
            {/* <div className="text-white text-center">
              <h1 className="text-4xl font-bold mb-4">Welcome Back</h1>
              <p className="text-xl">We're glad to see you again!</p>
            </div> */}
          </div>
        </div>
        <div className="w-full md:w-2/5 h-full flex items-center justify-center bg-white dark:bg-neutral-900">
          <div className="w-full  lg:px-20 p-8">
            {view === 'signin' && (
              <SignIn
                loginData={loginData}
                setLoginData={setLoginData}
                loading={loading}
                errorMessage={errorMessage}
                handleLogin={handleLogin}
                setView={setView}
              />
            )}

            {view === 'forgot' && (
              <ForgotPassword
                loginData={loginData}
                setLoginData={setLoginData}
                loading={loading}
                errorMessage={errorMessage}
                handleForgotPassword={handleForgotPassword}
                setView={setView}
              />
            )}
            {codeVerify && (
              <VerificationCode
                loginData={loginData}
                setLoginData={setLoginData}
                loading={loading}
                errorMessage={errorMessage}
                handleCodeVerification={view === 'forgot' ? handleForgetPassCodeVerification : handleCodeVerification}
                setView={setView}
              />
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

function SignIn({ loginData, setLoginData, loading, errorMessage, handleLogin, setView }) {
  const [passwordVisible, setPasswordVisible] = useState(false)

  return (
    <form onSubmit={handleLogin} className="space-y-6">
      <div className="space-y-2 text-start">
        <h1 className="text-3xl lg:text-4xl font-semibold text-neutral-800 dark:text-neutral-200">Sign In</h1>
        <p className="text-neutral-600 dark:text-neutral-400">Enter your email and password</p>
      </div>

      {errorMessage && (
        <div className="rounded-lg  bg-red-600/10 p-4 text-red-600">
          <p className="text-sm font-medium">{errorMessage}</p>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-left block text-neutral-700 dark:text-neutral-300">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400" size={20} />
            <input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={loginData.email || ''}
              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
              className="w-full rounded-lg bg-neutral-100 outline-none ring-none  px-4 h-12 py-2 text-neutral-800  "
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="text-sm font-medium text-left block text-neutral-700 dark:text-neutral-300">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={passwordVisible ? 'text' : 'password'}
              placeholder="••••••••••••"
              value={loginData.password || ''}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              className="w-full rounded-lg bg-neutral-100 outline-none ring-none  px-4 h-12 py-2 text-neutral-800  "
              required
            />
            <button
              type="button"
              onClick={() => setPasswordVisible(!passwordVisible)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400"
            >
              {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between text-sm">
        <div className="flex justify-start  items-center gap-2">
          <input type="checkbox" className="shrink-0 h-4 w-4 border-neutral-200 rounded text-black focus:ring-black disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-700 dark:checked:bg-black dark:checked:border-black dark:focus:ring-offset-neutral-800" id="hs-checked-checkbox" defaultChecked />
          <label htmlFor="hs-checked-checkbox" className="text-sm text-neutral-500 dark:text-neutral-400">Remenber me</label>
        </div>


        <button type="button" onClick={() => setView('forgot')} className="text-blue-500 underline">
          Forgot password?
        </button>
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-lg bg-neutral-900 px-4 py-2 h-12 text-center text-md font-semibold text-white transition-colors hover:bg-neutral-900/90 focus:outline-none focus:ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24"></svg>
            Signing In...
          </span>
        ) : (
          'Sign In'
        )}
      </button>


    </form>
  )
}

function ForgotPassword({ loginData, setLoginData, loading, errorMessage, handleForgotPassword, setView }) {
  return (
    <form onSubmit={handleForgotPassword} className="space-y-6">
      <div className="space-y-2 text-start">
        <h1 className="text-3xl lg:text-4xl font-semibold text-neutral-800 dark:text-neutral-200">Forgot Password</h1>
        <p className="text-neutral-600 dark:text-neutral-400">Enter your email to reset your password</p>
      </div>

      {errorMessage && (
        <div className="rounded-lg  bg-red-600/10 p-4 text-red-600">
          <p className="text-sm font-medium">{errorMessage}</p>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="email" className="text-sm font-medium text-left block text-neutral-700 dark:text-neutral-300">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 dark:text-neutral-400" size={20} />
            <input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={loginData.email || ''}
              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
              className="w-full rounded-lg bg-neutral-100 outline-none ring-none  px-4 h-12 py-2 text-neutral-800  "
              required
            />
          </div>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-lg bg-neutral-900 px-4 py-2 h-12 text-center text-md font-semibold text-white transition-colors hover:bg-neutral-900/90 focus:outline-none focus:ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24"></svg>
            Sending Reset Link...
          </span>
        ) : (
          'Send Reset Link'
        )}
      </button>

      <div className="text-center text-sm">
        <button type="button" onClick={() => setView('signin')} className="text-blue-500 underline">
          back to sign in
        </button>
      </div>
    </form>
  )
}

function VerificationCode({ loginData, setLoginData, loading, errorMessage, handleCodeVerification, setView }) {
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', ''])
  const inputRefs = useRef([])

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [])

  const handleInput = (e, index) => {
    const value = e.target.value
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newVerificationCode = [...verificationCode]
      newVerificationCode[index] = value
      setVerificationCode(newVerificationCode)
      setLoginData({ ...loginData, code: newVerificationCode.join('') })

      if (value.length === 1 && index < 5) {
        inputRefs.current[index + 1].focus()
      }
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1].focus()
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const paste = e.clipboardData.getData('text')
    if (paste.length === 6 && /^[0-9]*$/.test(paste)) {
      const newVerificationCode = paste.split('')
      setVerificationCode(newVerificationCode)
      setLoginData({ ...loginData, code: paste })
      inputRefs.current[5].focus()
    }
  }

  return (
    <form onSubmit={handleCodeVerification} className="space-y-6">
      <div className="space-y-2 text-start">
        <h1 className="text-3xl lg:text-4xl font-semibold text-neutral-800 dark:text-neutral-200">Verify Code</h1>
        <p className="text-neutral-600 dark:text-neutral-400">Enter the verification code sent to your email</p>
      </div>

      {errorMessage && (
        <div className="rounded-lg  bg-red-600/10 p-4 text-red-600">
          <p className="text-sm font-medium">{errorMessage}</p>
        </div>
      )}

      <div className="flex justify-center space-x-2">
        {verificationCode.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            maxLength={1}
            value={digit}
            onChange={(e) => handleInput(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            className="w-full rounded-lg bg-neutral-100 outline-none ring-none w-full h-12  text-neutral-800  "
          />
        ))}
      </div>

      <button
        type="submit"
        disabled={loading || verificationCode.join('').length !== 6}
        className="w-full rounded-lg bg-neutral-900 px-4 py-2 h-12 text-center text-md font-semibold text-white transition-colors hover:bg-neutral-900/90 focus:outline-none focus:ring disabled:cursor-not-allowed disabled:opacity-50"
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24"></svg>
            Verifying...
          </span>
        ) : (
          'Verify'
        )}
      </button>

      <div className="text-center text-sm">
        <button type="button" onClick={() => setView('signin')} className="text-blue-500 underline">
          Back to Sign In
        </button>
      </div>
    </form>
  )
}

export default Login

